import { useState, useEffect } from "react";
import clsx from "clsx";
import { WidgetContainer } from "@/layout-components/WidgetContainer";
import { getWidgetById } from "@/utils/widgets";
import NewsquawkHeadlines from "@/widget-components/newsquawk-headlines/NewsquawkHeadlines";
import { PortfolioBuilderProvider, usePortfolioBuilder } from "./components/PortfolioBuilderContext";
import { useDensity } from "@/contexts/DensityContext";
import PortfolioBuilderBrowser from "./components/PortfolioBuilderBrowser";
import useAuth from "@/hooks/useAuth";
import DemoBanner from "@/ui-components/DemoBanner";
import { usePortfoliosCRUD } from "./api/usePortfoliosCRUD";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { formatListName } from "@/shared/utils/formatListName";

/** Names what is being previewed, so the strip says whose headlines these are. */
function PreviewInfo() {
  const { previewState, clearPreview } = usePortfolioBuilder();
  const { portfolios } = usePortfoliosCRUD();
  const { watchlistsQuery } = useTagsWatchlistsCRUD();
  const { queriesQuery } = useHeadlineQueriesCRUD();

  const name =
    previewState.type === "watchlist"
      ? portfolios.find((p) => p.id === previewState.id)?.name
      : previewState.type === "tagWatchlist"
        ? watchlistsQuery.data?.find((w) => w.id === previewState.id)?.name
        : queriesQuery.data?.find((q) => q.id === previewState.id)?.queryName;

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-squawk-blue">
        {name
          ? `Previewing “${formatListName(name)}”`
          : previewState.type === "filter"
            ? "Previewing combined watchlist"
            : "Previewing watchlist"}
      </span>
      <button
        onClick={clearPreview}
        className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
      >
        Clear
      </button>
    </div>
  );
}

/**
 * The headline strip under the builder.
 *
 * Always there, always the same size; a click on a list changes what it
 * shows, never whether it is there. Hiding it until something was picked
 * bought the lists more height, but at the price of a widget appearing out of
 * nowhere and the page reflowing under the pointer — a surprise on the one
 * action people take most here. With nothing picked it is the general feed,
 * and says so.
 *
 * Shown at every width when signed in, since clicking a list previews it and
 * that has to land somewhere. Logged-out visitors have no lists to pick, so
 * they keep the demo's feed on wide screens only, as before.
 */
function PreviewArea({ isXlScreen }: { isXlScreen: boolean }) {
  const { previewState } = usePortfolioBuilder();
  const { authenticated } = useAuth();
  const previewing = previewState.type !== null;

  const headlines = (
    <WidgetContainer
      widget={getWidgetById("NewsquawkHeadlines")!}
      onClose={() => {}}
    >
      {(headerOptions) => <NewsquawkHeadlines headerOptions={headerOptions} />}
    </WidgetContainer>
  );

  if (!authenticated && !isXlScreen) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {(previewing || authenticated) && (
        <div className="flex h-6 shrink-0 items-center px-1 pb-1">
          {previewing ? (
            <PreviewInfo />
          ) : (
            <p className="text-xs text-gray-500">
              Showing all headlines — click a watchlist above to see what it
              catches.
            </p>
          )}
        </div>
      )}
      <div className="min-h-0 flex-1">{headlines}</div>
    </div>
  );
}

function WishlistAndFilterBuilder() {
  const { isCompact } = useDensity();
  const { authenticated } = useAuth();
  const [isXlScreen, setIsXlScreen] = useState(() => window.innerWidth >= 1280);

  // Track screen size
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1280px)");
    const handleChange = (e: MediaQueryListEvent) => setIsXlScreen(e.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return (
    <PortfolioBuilderProvider>
      {!authenticated && <DemoBanner />}
      <div className={clsx(
        "h-screen flex flex-col overflow-hidden",
        "gap-2 p-2",
        isCompact && "gap-1 p-1",
        !authenticated && "h-[calc(100vh-42px)]"
      )}>
        <PortfolioBuilderBrowser />
        <PreviewArea isXlScreen={isXlScreen} />
      </div>
    </PortfolioBuilderProvider>
  );
}

export default WishlistAndFilterBuilder;
